import { useMutation } from '@apollo/client';
import omit from 'lodash.omit';
import { Button } from '@m/alchemy-ui/Button';
import { Toast } from '@m/alchemy-ui/Toast';
import { Form, Formik, type FormikErrors } from 'formik';
import { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { DEFAULT_LANGUAGE } from '../shared-constants';
import { type AppProps, type Translation } from '../../common/interfaces';
import { CREATE_CUSTOM_PAGE } from '../../graphql/mutations';
import { getErrorToastMessage } from '../../helpers';
import {
  type CreateCustomPageMutation,
  type CreateCustomPageMutationVariables
} from '../../graphql/mutations/__generated__/page.generated';
import { type ToastMessageType } from '../../common/enums';
import { customPageTitleMessages, CustomPageTitleRow } from './components/CustomPageTitleRow';
import { Actions, IntroText, Wrapper } from './CustomPage.styled';

const messages = defineMessages({
  intro: {
    id: 'newCustomPageIntro',
    defaultMessage:
      'Custom pages can display elements with text, carousels, images and activity lists in any order you like.'
  },
  continueButton: {
    id: 'newCustomPageContinueButton',
    defaultMessage: 'Continue'
  },
  submitError: {
    id: 'somethingWentWrong',
    defaultMessage: 'Something went wrong.'
  }
});

interface NewPageFormValues {
  language: string;
  pageNavTitle: string;
  isExclude: boolean;
  translations: Translation[];
}

export const NewCustomPage = (_props: AppProps) => {
  const intl = useIntl();
  const [toastMessages, setToastMessages] = useState<ToastMessageType[]>([]);
  const [createCustomPage, { loading }] = useMutation<CreateCustomPageMutation, CreateCustomPageMutationVariables>(
    CREATE_CUSTOM_PAGE
  );

  const requiredMessage = intl.formatMessage(customPageTitleMessages.requiredTitleError);

  const initialValues: NewPageFormValues = {
    language: DEFAULT_LANGUAGE,
    translations: [],
    pageNavTitle: '',
    isExclude: false
  };

  const validate = (values: NewPageFormValues) => {
    const errors: FormikErrors<NewPageFormValues> = {};
    setToastMessages([]);
    if (!values.pageNavTitle.trim()) {
      errors.pageNavTitle = requiredMessage;
    }
    return errors;
  };

  return (
    <Wrapper data-testid="new-page-view">
      <IntroText>{intl.formatMessage(messages.intro)}</IntroText>

      <Formik<NewPageFormValues>
        initialValues={initialValues}
        validate={validate}
        onSubmit={async (values) => {
          try {
            const result = await createCustomPage({
              variables: {
                input: {
                  title: values.pageNavTitle,
                  isExclude: values.isExclude,
                  translations:
                    values.translations.length > 0
                      ? values.translations.map((translation) => omit(translation, 'communityId'))
                      : undefined
                }
              }
            });
            const newId = result.data?.createCustomPage;
            if (newId) {
              window.location.href = `/page/admin_edit/${newId}`;
            }
          } catch {
            setToastMessages(getErrorToastMessage(intl.formatMessage(messages.submitError)));
          }
        }}
      >
        {() => (
          <Form>
            <CustomPageTitleRow />

            <Toast messages={toastMessages} />

            <Actions>
              <Button type="submit" priority="primary" isLoading={loading}>
                {intl.formatMessage(messages.continueButton)}
              </Button>
            </Actions>
          </Form>
        )}
      </Formik>
    </Wrapper>
  );
};
